// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { Plus, MapPin, Edit, Trash2, Home, Building, Loader2, ShieldCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const addressSchema = z.object({
  address_type: z.enum(['shipping', 'billing']),
  label: z.string().min(1, 'Label is required').max(100),
  first_name: z.string().min(1, 'First name is required').max(100),
  last_name: z.string().min(1, 'Last name is required').max(100),
  company: z.string().max(100).optional(),
  address_line_1: z.string().min(1, 'Address is required'),
  address_line_2: z.string().optional(),
  city: z.string().min(1, 'City is required').max(100),
  state_province: z.string().min(1, 'State/Province is required').max(100),
  postal_code: z.string().min(1, 'Postal code is required').max(20),
  country: z.string().min(1, 'Country is required').max(100),
  phone: z.string().max(20).optional(),
  is_primary: z.boolean().default(false),
});

type AddressFormData = z.infer<typeof addressSchema>;

interface Address extends AddressFormData {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 
  'France', 'Italy', 'Spain', 'Netherlands', 'Switzerland'
];

const ADDRESS_TYPE_LABELS = {
  shipping: 'Shipping',
  billing: 'Billing'
};

const ADDRESS_TYPE_ICONS = {
  shipping: Home,
  billing: Building
};

export default function MultipleAddressManager() {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingPrimaryId, setUpdatingPrimaryId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      address_type: 'shipping',
      country: 'United States',
      is_primary: false,
    }
  });

  const watchedAddressType = watch('address_type');

  useEffect(() => {
    loadAddresses();
  }, [user?.id]);

  const resetFormDefaults = () => {
    reset({
      address_type: 'shipping',
      country: 'United States',
      is_primary: false,
      label: '',
      first_name: '',
      last_name: '',
      company: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state_province: '',
      postal_code: '',
      phone: '',
    });
  };

  const loadAddresses = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_addresses')
        .select('*')
        .eq('user_id', user.id)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;
      setAddresses(data || []);
    } catch (error: any) {
      console.error('Error loading addresses:', error);
      toast({
        title: "Error",
        description: "Failed to load addresses",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: AddressFormData) => {
    if (!user?.id) return;

    setSubmitting(true);
    try {
      const addressData = {
        ...data,
        user_id: user.id,
        company: data.company || null,
        address_line_2: data.address_line_2 || null,
        phone: data.phone || null,
      };

      let result;
      if (editingAddress) {
        result = await supabase
          .from('user_addresses')
          .update(addressData)
          .eq('id', editingAddress.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('user_addresses')
          .insert(addressData)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      toast({
        title: "Success",
        description: editingAddress ? "Address updated successfully" : "Address added successfully",
      });

      await loadAddresses();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving address:', error);
      toast({
        title: "Error",
        description: "Failed to save address: " + error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAddressClick = () => {
    setEditingAddress(null);
    resetFormDefaults();
    setIsDialogOpen(true);
  };

  const handleEditAddress = (address: Address) => {
    setEditingAddress(address);
    reset({
      ...address,
      address_type: address.address_type === 'primary' ? 'shipping' : address.address_type,
      company: address.company || '',
      address_line_2: address.address_line_2 || '',
      phone: address.phone || '',
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAddress(null);
    resetFormDefaults();
  };

  const handleSetPrimaryShipping = async (address: Address) => {
    if (!user?.id) return;

    try {
      setUpdatingPrimaryId(address.id);

      const { error: clearError } = await supabase
        .from('user_addresses')
        .update({ is_primary: false })
        .eq('user_id', user.id)
        .eq('address_type', 'shipping');

      if (clearError) throw clearError;

      const { error } = await supabase
        .from('user_addresses')
        .update({ is_primary: true })
        .eq('id', address.id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: 'Primary Shipping Updated',
        description: `${address.label} is now your primary shipping destination.`,
      });

      await loadAddresses();
    } catch (error: any) {
      console.error('Error updating primary shipping address:', error);
      toast({
        title: 'Error',
        description: 'Failed to set primary shipping address: ' + error.message,
        variant: 'destructive',
      });
    } finally {
      setUpdatingPrimaryId(null);
    }
  };

  const deleteAddress = async (addressId: string, isPrimary: boolean) => {
    if (isPrimary) {
      toast({
        title: 'Primary Address Locked',
        description: 'Set another shipping address as primary before deleting this one.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setDeletingId(addressId);

      const { error } = await supabase
        .from('user_addresses')
        .delete()
        .eq('id', addressId)
        .eq('user_id', user?.id ?? '');

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Address deleted successfully',
      });

      await loadAddresses();
    } catch (error: any) {
      console.error('Error deleting address:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete address: ' + error.message,
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <h3 className="text-xl font-semibold text-white">Shipping Addresses</h3>
          <p className="text-white/60 text-sm max-w-2xl">
            Manage every delivery destination connected to your account. Primary shipping syncs with profile details and checkout for a seamless experience.
          </p>
        </div>
        <Button
          onClick={handleAddAddressClick}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Address
        </Button>
      </div>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDialog();
          } else {
            setIsDialogOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-2xl bg-slate-950/95 backdrop-blur-xl border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">
              {editingAddress ? 'Edit Address' : 'Add Address'}
            </DialogTitle>
            <DialogDescription className="text-white/70">
              Complete the details below. Marking an address as primary shipping overrides the destination used for deliveries and invoices.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address_type" className="text-white">Address Type</Label>
                <Select
                  value={watchedAddressType}
                  onValueChange={(value) => setValue('address_type', value as any)}
                >
                  <SelectTrigger className="bg-slate-900 border-white/10 text-white">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {Object.entries(ADDRESS_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value} className="text-white">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="label" className="text-white">Address Nickname</Label>
                <Input
                  id="label"
                  {...register('label')}
                  placeholder="e.g., Home, Gallery, Storage"
                  className="bg-slate-900 border-white/10 text-white"
                />
                {errors.label && <p className="text-red-400 text-sm">{errors.label.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-white">First Name</Label>
                <Input
                  id="first_name"
                  {...register('first_name')}
                  className="bg-slate-900 border-white/10 text-white"
                />
                {errors.first_name && <p className="text-red-400 text-sm">{errors.first_name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-white">Last Name</Label>
                <Input
                  id="last_name"
                  {...register('last_name')}
                  className="bg-slate-900 border-white/10 text-white"
                />
                {errors.last_name && <p className="text-red-400 text-sm">{errors.last_name.message}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-white">Company (Optional)</Label>
              <Input
                id="company"
                {...register('company')}
                className="bg-slate-900 border-white/10 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line_1" className="text-white">Address Line 1</Label>
              <Input
                id="address_line_1"
                {...register('address_line_1')}
                className="bg-slate-900 border-white/10 text-white"
              />
              {errors.address_line_1 && <p className="text-red-400 text-sm">{errors.address_line_1.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line_2" className="text-white">Address Line 2 (Optional)</Label>
              <Input
                id="address_line_2"
                {...register('address_line_2')}
                className="bg-slate-900 border-white/10 text-white"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-white">City</Label>
                <Input
                  id="city"
                  {...register('city')}
                  className="bg-slate-900 border-white/10 text-white"
                />
                {errors.city && <p className="text-red-400 text-sm">{errors.city.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="state_province" className="text-white">State/Province</Label>
                <Input
                  id="state_province"
                  {...register('state_province')}
                  className="bg-slate-900 border-white/10 text-white"
                />
                {errors.state_province && <p className="text-red-400 text-sm">{errors.state_province.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="postal_code" className="text-white">Postal Code</Label>
                <Input
                  id="postal_code"
                  {...register('postal_code')}
                  className="bg-slate-900 border-white/10 text-white"
                />
                {errors.postal_code && <p className="text-red-400 text-sm">{errors.postal_code.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country" className="text-white">Country</Label>
                <Select
                  value={watch('country')}
                  onValueChange={(value) => setValue('country', value)}
                >
                  <SelectTrigger className="bg-slate-900 border-white/10 text-white">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country} className="text-white">
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.country && <p className="text-red-400 text-sm">{errors.country.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white">Phone (Optional)</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  type="tel"
                  className="bg-slate-900 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 rounded-2xl bg-slate-900/60 border border-white/10 px-4 py-3">
              <Switch
                id="is_primary"
                checked={watch('is_primary')}
                onCheckedChange={(checked) => setValue('is_primary', checked)}
              />
              <div>
                <Label htmlFor="is_primary" className="text-white font-medium">
                  Set as primary {watchedAddressType} address
                </Label>
                <p className="text-xs text-white/50">Primary shipping is surfaced across checkout, invoices, and delivery updates.</p>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingAddress ? 'Update Address' : 'Add Address'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {addresses.length === 0 ? (
        <Card className="bg-slate-900/70 border border-white/10">
          <CardContent className="text-center py-10 space-y-3">
            <MapPin className="h-12 w-12 text-white/30 mx-auto" />
            <h4 className="text-lg text-white font-medium">No addresses yet</h4>
            <p className="text-white/50 text-sm max-w-md mx-auto">
              Add your first shipping address to streamline checkout and shipping logistics.
            </p>
            <Button
              onClick={handleAddAddressClick}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Address
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {addresses.map((address) => {
            const Icon = ADDRESS_TYPE_ICONS[address.address_type as keyof typeof ADDRESS_TYPE_ICONS];
            const isPrimaryShipping = address.is_primary && address.address_type === 'shipping';
            return (
              <Card
                key={address.id}
                className="bg-slate-900/80 border border-white/10 shadow-xl hover:shadow-emerald-500/20 transition-all duration-300"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-emerald-300" />
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg flex items-center gap-2">
                          {address.label}
                          {isPrimaryShipping && (
                            <span className="text-xs font-semibold text-emerald-200 bg-emerald-500/20 px-2 py-1 rounded-full flex items-center gap-1">
                              <ShieldCheck className="h-3 w-3" />
                              Primary Shipping
                            </span>
                          )}
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <Badge
                            variant="secondary"
                            className="text-xs bg-white/10 text-white border-white/20"
                          >
                            {ADDRESS_TYPE_LABELS[address.address_type as keyof typeof ADDRESS_TYPE_LABELS]}
                          </Badge>
                          {!isPrimaryShipping && address.is_primary && (
                            <Badge
                              variant="secondary"
                              className="text-xs bg-emerald-500/15 text-emerald-300 border-emerald-500/20"
                            >
                              Primary
                            </Badge>
                          )}
                          {address.address_type === 'shipping' && !address.is_primary && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetPrimaryShipping(address)}
                              disabled={updatingPrimaryId === address.id}
                              className="h-8 text-xs border-emerald-400/30 text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
                            >
                              {updatingPrimaryId === address.id ? (
                                <>
                                  <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                                  Setting Primary
                                </>
                              ) : (
                                'Set Primary Shipping'
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditAddress(address)}
                        className="text-white/60 hover:text-white hover:bg-white/10"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteAddress(address.id, isPrimaryShipping)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        disabled={deletingId === address.id}
                      >
                        {deletingId === address.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-white/70 text-sm leading-relaxed space-y-1">
                  <p className="font-medium text-white">{address.first_name} {address.last_name}</p>
                  {address.company && <p>{address.company}</p>}
                  <p>{address.address_line_1}</p>
                  {address.address_line_2 && <p>{address.address_line_2}</p>}
                  <p>{address.city}, {address.state_province} {address.postal_code}</p>
                  <p>{address.country}</p>
                  {address.phone && <p>{address.phone}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}